import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import messages from "../../../assets/locale/messages";
import { getCompanyPortalCountries } from "../../../network/apis/companyPortalEventRequests";
import { getCompanyPortalLookups } from "../../../network/apis/companyPortalEvents";
import { extractPortalLookupArray } from "../../../utils/companyPortal/portalEventsList";
import { lookupsKeys } from "../../../utils/Constants";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { normalizeCountryOptions } from "../../../utils/countryOptions";
import { showErrorMsg } from "../../../utils/Helpers";
import {
  normalizeRoleLookupOptions,
  readUserTagName,
} from "../../../components/Events/Event/Applicants/eventRequestMappers";
import {
  canManageTagRow,
  getTagEligibility,
} from "../../../components/Events/Event/Applicants/tagEligibility";
import useApplicantTags from "../../../components/Events/Event/Applicants/hooks/company/useApplicantTags";
import {
  getLoyalApplicantsRequest,
  bulkRemoveLoyalApplicantsRequest,
} from "../../../store/CompanyPortal/LoyalApplicants/actions";

const PORTAL_VARIANT = "company-portal";
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 450;
const COUNTRY_SEARCH_MIN_LENGTH = 3;

const useLoyalApplicants = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const myCompanyId = useSelector((state) => state.auth.login?.company?.id);
  const { list, meta } = useSelector((state) => state.companyLoyalApplicants);
  const { applicants, shared } = messages[lang];
  const copy = applicants.loyalApplicants;

  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [nationalityFilter, setNationalityFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState([]);

  const [nationalityOptions, setNationalityOptions] = useState([]);
  const [nationalitySearch, setNationalitySearch] = useState("");
  const [debouncedNationalitySearch, setDebouncedNationalitySearch] = useState("");
  const [nationalityLoading, setNationalityLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  const [removeModal, setRemoveModal] = useState({ open: false });

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchValue), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchValue]);

  const listParams = useMemo(
    () => ({
      page,
      items: pageSize,
      ...(debouncedSearch ? { query: debouncedSearch } : {}),
      ...(nationalityFilter?.id ? { nationality_id: nationalityFilter.id } : {}),
      ...(roleFilter?.id ? { role_id: roleFilter.id } : {}),
      ...(tagFilter?.id ? { tag_id: tagFilter.id } : {}),
    }),
    [page, pageSize, debouncedSearch, nationalityFilter, roleFilter, tagFilter],
  );

  useEffect(() => {
    dispatch(getLoyalApplicantsRequest(listParams));
  }, [dispatch, listParams]);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedNationalitySearch(nationalitySearch),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(id);
  }, [nationalitySearch]);

  useEffect(() => {
    let mounted = true;
    const query = debouncedNationalitySearch.trim();
    const selectedNationalityName = nationalityFilter?.name || "";
    const isSelectedNationalitySearch =
      selectedNationalityName &&
      query.toLowerCase() === selectedNationalityName.toLowerCase();
    const shouldSearchCountries = !query || query.length >= COUNTRY_SEARCH_MIN_LENGTH;

    if (isSelectedNationalitySearch || !shouldSearchCountries) return undefined;

    setNationalityLoading(true);
    getCompanyPortalCountries(
      { page: -1, ...(query ? { name: query } : {}) },
      { handlerEnabled: false },
    )
      .then((response) => {
        if (mounted) setNationalityOptions(normalizeCountryOptions(response.data?.data, lang));
      })
      .catch((error) => showErrorMsg(error.response?.data?.message))
      .finally(() => { if (mounted) setNationalityLoading(false); });

    return () => { mounted = false; };
  }, [debouncedNationalitySearch, nationalityFilter, lang]);

  useEffect(() => {
    let mounted = true;
    getCompanyPortalLookups([lookupsKeys.roles, lookupsKeys.tags])
      .then((response) => {
        if (!mounted) return;
        setRoleOptions(
          normalizeRoleLookupOptions(
            extractPortalLookupArray(response, lookupsKeys.roles),
            lang,
          ),
        );
        setTagOptions(
          normalizeRoleLookupOptions(
            extractPortalLookupArray(response, lookupsKeys.tags),
            lang,
          ),
        );
      })
      .catch((error) => { if (mounted) showErrorMsg(error.response?.data?.message); });
    return () => { mounted = false; };
  }, [lang]);

  const rows = useMemo(
    () =>
      list.map((item) => ({
        id: item.id,
        userId: item.id,
        image: item.image,
        nameText:
          lang === "en"
            ? item.full_name_en?.trim() || item.full_name_ar?.trim() || "-"
            : item.full_name_ar?.trim() || item.full_name_en?.trim() || "-",
        nationality: item.nationality?.name || "-",
        role: item.role?.name || "-",
        rating: item.rating ?? 0,
        userTag: item.user_tag || null,
        tagName: readUserTagName(item.user_tag, lang),
      })),
    [list, lang],
  );

  const tagControls = useApplicantTags({
    rows,
    selectedIds,
    rowsForBulkTag: rows,
    portalVariant: PORTAL_VARIANT,
    myCompanyId,
    listParams,
    refreshList: (params) => dispatch(getLoyalApplicantsRequest(params)),
  });

  const selectedApplicants = useMemo(
    () => rows.filter((row) => selectedIds.includes(row.id)),
    [rows, selectedIds],
  );
  const assignEligibility = getTagEligibility(selectedApplicants, {
    portalVariant: PORTAL_VARIANT,
    myCompanyId,
    mode: "assign",
  });
  const removeEligibility = getTagEligibility(selectedApplicants, {
    portalVariant: PORTAL_VARIANT,
    myCompanyId,
    mode: "remove",
  });
  const isEditTagDisabled =
    assignEligibility.total === 0 ||
    assignEligibility.otherOwnerCount === assignEligibility.total;
  const isDeleteTagDisabled =
    removeEligibility.eligibleUserIds.length === 0 &&
    removeEligibility.otherOwnerCount > 0;
  const canManageRowTag = (row) =>
    canManageTagRow(row, { portalVariant: PORTAL_VARIANT, myCompanyId });

  const handleSearchChange = (value) => {
    setSearchValue(typeof value === "string" ? value : "");
    setPage(DEFAULT_PAGE);
    setSelectedIds([]);
  };

  const handleNationalityInputChange = (_event, value, reason) => {
    if (reason === "input" || reason === "clear" || reason === "reset") {
      setNationalitySearch(value || "");
    }
  };

  const handleNationalityOpen = () => {
    setNationalityLoading(true);
    getCompanyPortalCountries({ page: -1 }, { handlerEnabled: false })
      .then((response) => {
        setNationalityOptions(normalizeCountryOptions(response.data?.data, lang));
      })
      .catch((error) => showErrorMsg(error.response?.data?.message))
      .finally(() => setNationalityLoading(false));
  };

  const handleNationalityChange = (value) => {
    if (!value) {
      setNationalitySearch("");
      setNationalityFilter(null);
      setPage(DEFAULT_PAGE);
      setSelectedIds([]);
      return;
    }
    // eslint-disable-next-line no-unused-vars
    const { icon: _icon, ...cleanValue } = value;
    setNationalitySearch(cleanValue.name || "");
    setNationalityFilter(cleanValue);
    setPage(DEFAULT_PAGE);
    setSelectedIds([]);
  };

  const handleRoleChange = (value) => {
    setRoleFilter(value);
    setPage(DEFAULT_PAGE);
    setSelectedIds([]);
  };

  const handleTagFilterChange = (value) => {
    setTagFilter(value);
    setPage(DEFAULT_PAGE);
    setSelectedIds([]);
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(DEFAULT_PAGE);
  };

  const handleSelectionChange = (ids) => setSelectedIds(ids);

  const handleRowClick = (row) => {
    const applicantId = row?.userId ?? row?.id;
    if (!applicantId) return;

    history.push({
      pathname: ROUTE_PATHS.companyViewApplicantProfile.replace(":id", applicantId),
      state: { fromLoyalApplicants: true },
    });
  };

  const handleRemoveClick = () => setRemoveModal({ open: true });
  const handleRemoveClose = () => setRemoveModal({ open: false });

  const handleRemoveConfirm = () => {
    dispatch(
      bulkRemoveLoyalApplicantsRequest({
        userIds: selectedIds,
        listParams,
        onSuccess: () => setSelectedIds([]),
      }),
    );
    handleRemoveClose();
  };

  const paginationTotal = meta.count;
  const paginationCurrentPage = meta.currentPage;
  const paginationPages = meta.pages;
  const paginationFrom =
    paginationTotal === 0 ? 0 : (paginationCurrentPage - 1) * pageSize + 1;
  const paginationTo = Math.min(paginationCurrentPage * pageSize, paginationTotal);

  return {
    lang,
    copy,
    shared,
    rows,
    pageSize,
    selectedIds,
    selectedApplicants,
    nationalityFilter,
    nationalityOptions,
    nationalitySearch,
    nationalityLoading,
    roleFilter,
    roleOptions,
    tagFilter,
    tagOptions,
    searchValue,
    removeModal,
    myCompanyId,
    portalVariant: PORTAL_VARIANT,
    tagControls,
    isEditTagDisabled,
    isDeleteTagDisabled,
    canManageRowTag,
    paginationTotal,
    paginationCurrentPage,
    paginationPages,
    paginationFrom,
    paginationTo,
    onSearchChange: handleSearchChange,
    onNationalityInputChange: handleNationalityInputChange,
    onNationalityOpen: handleNationalityOpen,
    onNationalityChange: handleNationalityChange,
    onRoleChange: handleRoleChange,
    onTagFilterChange: handleTagFilterChange,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    onSelectionChange: handleSelectionChange,
    onRowClick: handleRowClick,
    onTagClick: tagControls.openApplicantTag,
    onBulkEditTags: tagControls.openBulkTag,
    onBulkDeleteTags: tagControls.openDeleteBulkTag,
    onRemoveClick: handleRemoveClick,
    onRemoveClose: handleRemoveClose,
    onRemoveConfirm: handleRemoveConfirm,
  };
};

export default useLoyalApplicants;
